"use client";

import React, { useState, useEffect, useRef } from "react";
import { MenuNode } from "./menuAdapter";
import MegaMenuPanel from "./MegaMenuPanel";

interface MegaMenuControllerProps {
  isOpen: boolean;
  parentCategory: MenuNode;
  lookupMap: Map<string, MenuNode>;
  align?: "left" | "right";
  onClose: () => void;
}

export default function MegaMenuController({
  isOpen,
  parentCategory,
  lookupMap,
  align = "left",
  onClose,
}: MegaMenuControllerProps) {
  const [activePath, setActivePath] = useState<string[]>([]);
  const [hoveredPath, setHoveredPath] = useState<string[]>([]);

  // Debouncing refs using ReturnType<typeof setTimeout> to avoid environment-specific NodeJS.Timeout conflicts
  const openTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const controllerRef = useRef<HTMLDivElement | null>(null);

  // 1. Keyboard accessibility listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        // No action on Escape to keep megamenu open
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Reset active state when menu closes or parent changes
  useEffect(() => {
    setActivePath([]);
    setHoveredPath([]);
  }, [isOpen, parentCategory]);

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      if (openTimeoutRef.current) clearTimeout(openTimeoutRef.current);
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  if (!isOpen || !parentCategory.children || parentCategory.children.length === 0) {
    return null;
  }

  // Handle hovered item inside a column (with open timing debounce)
  const handleItemHover = (level: number, node: MenuNode) => {
    // Clear any pending open timers
    if (openTimeoutRef.current) clearTimeout(openTimeoutRef.current);
    // Clear close timers
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);

    // Temp hovered path highlight (instant visual feedback)
    setHoveredPath((prev) => {
      const next = [...prev.slice(0, level)];
      next[level] = node.id;
      return next;
    });

    // Debounce the panel depth update (open delay: 80ms)
    openTimeoutRef.current = setTimeout(() => {
      setActivePath((prev) => {
        const next = [...prev.slice(0, level)];
        next[level] = node.id;
        return next;
      });
    }, 80);
  };

  const handlePanelMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
  };

  return (
    <div
      ref={controllerRef}
      onPointerEnter={handlePanelMouseEnter}
      onPointerLeave={() => {}}
    >
      <MegaMenuPanel
        title={parentCategory.label}
        categories={parentCategory.children}
        activePath={activePath}
        hoveredPath={hoveredPath}
        lookupMap={lookupMap}
        align={align}
        onItemHover={handleItemHover}
        // onItemClick={() => {}}
        onClose={onClose}
      />
    </div>
  );
}
